import { useEffect } from "react";

/**
 * Adds `.in-view` to any element with a reveal* class once it enters the viewport.
 * Supports staggered children via `data-stagger` attribute (delay in ms between children).
 * Honors prefers-reduced-motion.
 */
export default function useScrollReveal() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const selectors = [
      ".reveal",
      ".reveal-up",
      ".reveal-left",
      ".reveal-right",
      ".reveal-scale",
      ".reveal-blur",
      ".reveal-rotate",
    ].join(",");

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      document
        .querySelectorAll(selectors)
        .forEach((el) => el.classList.add("in-view"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const el = entry.target;
            el.classList.add("in-view");
            // Stagger children
            const staggerDelay = Number(el.dataset.stagger || 0);
            if (staggerDelay > 0) {
              const kids = el.querySelectorAll("[data-reveal-child]");
              kids.forEach((kid, idx) => {
                kid.style.transitionDelay = `${idx * staggerDelay}ms`;
                kid.classList.add("in-view");
              });
            }
            io.unobserve(el);
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    const observe = () => {
      document
        .querySelectorAll(`${selectors}`)
        .forEach((el) => {
          if (!el.classList.contains("in-view")) io.observe(el);
        });
    };

    observe();
    const t = setTimeout(observe, 400);
    return () => {
      clearTimeout(t);
      io.disconnect();
    };
  }, []);
}
